import { useEffect, useRef, useState } from 'react';

/**
 * SMG custom cursor — colored dot follows mouse tightly,
 * lerp-trailing ring scales up on interactive elements.
 * Disabled on touch/coarse pointer devices.
 */
export default function CustomCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    // Skip on touch devices
    if (typeof window === 'undefined') return;
    const isCoarse = window.matchMedia('(pointer: coarse)').matches;
    if (isCoarse) return;
    setEnabled(true);

    let raf;
    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let ringX = mouseX;
    let ringY = mouseY;

    const onMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${mouseX}px, ${mouseY}px, 0) translate(-50%, -50%)`;
      }
    };

    const tick = () => {
      ringX += (mouseX - ringX) * 0.18;
      ringY += (mouseY - ringY) * 0.18;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${ringX}px, ${ringY}px, 0) translate(-50%, -50%)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onEnter = () => setHovering(true);
    const onLeave = () => setHovering(false);
    const isHoverTarget = (el) => {
      if (!el || !el.matches) return false;
      return el.matches('a, button, [role="button"], [data-cursor-hover], input, textarea, select, label');
    };
    const onPointerOver = (e) => { if (isHoverTarget(e.target)) setHovering(true); };
    const onPointerOut = (e) => { if (isHoverTarget(e.target)) setHovering(false); };

    window.addEventListener('mousemove', onMove);
    document.addEventListener('pointerover', onPointerOver, true);
    document.addEventListener('pointerout', onPointerOut, true);
    // Hide native cursor
    document.documentElement.style.cursor = 'none';

    return () => {
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('pointerover', onPointerOver, true);
      document.removeEventListener('pointerout', onPointerOut, true);
      cancelAnimationFrame(raf);
      document.documentElement.style.cursor = '';
    };
  }, []);

  if (!enabled) return null;

  return (
    <>
      <div
        ref={dotRef}
        data-testid="smg-cursor-dot"
        className="fixed top-0 left-0 pointer-events-none z-[9999] w-[6px] h-[6px] rounded-full bg-[#FF3D2E]"
        style={{ mixBlendMode: 'difference' }}
      />
      <div
        ref={ringRef}
        data-testid="smg-cursor-ring"
        className={`fixed top-0 left-0 pointer-events-none z-[9998] rounded-full border border-white/85 transition-[width,height,background-color,border-color] duration-200 ease-out ${
          hovering ? 'w-14 h-14 bg-white/10 border-[#FF3D2E]' : 'w-8 h-8 bg-transparent'
        }`}
        style={{ mixBlendMode: 'difference' }}
      />
      <style>{`
        a, button, [data-cursor-hover] { cursor: none; }
      `}</style>
    </>
  );
}
